import React, { useRef, useState } from 'react';
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCheckbox, IonInput, IonItem, IonSelect, IonSelectOption } from '@ionic/react';
import { DeclineReason, ServiceEstimate } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import SignaturePad, { SignaturePadHandle } from './SignaturePad';
import { money, shortDate } from './serviceFormat';
import './AcceptancePanel.css';

interface Props {
  estimate: ServiceEstimate;
  onDone: (e: ServiceEstimate) => void;
  notify: (msg: string) => void;
}

const REASONS: [DeclineReason, string][] = [['price', 'Price / budget'], ['timing', 'Timing / funding'], ['competitor', 'Competitor'], ['no_decision', 'No decision'], ['other', 'Other']];

/** The customer signs on the tech's device, or the tech records a decline. */
const AcceptancePanel: React.FC<Props> = ({ estimate, onDone, notify }) => {
  const pad = useRef<SignaturePadHandle>(null);
  const [name, setName] = useState(estimate.contact_name || '');
  const [title, setTitle] = useState('');
  const [agree, setAgree] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState<DeclineReason | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (estimate.status === 'accepted') {
    return (
      <IonCard className="acceptance">
        <IonCardHeader><IonCardTitle>Customer acceptance</IonCardTitle></IonCardHeader>
        <IonCardContent>
          <div className="acceptance-done">
            <strong>Accepted {shortDate(estimate.accepted_at)}</strong>
            <div>{estimate.accepted_name}{estimate.accepted_title ? `, ${estimate.accepted_title}` : ''} · {estimate.customer_detail.name}</div>
            {estimate.signature_url && <img src={estimate.signature_url} alt="Customer signature" />}
          </div>
        </IonCardContent>
      </IonCard>
    );
  }
  if (estimate.status === 'declined') {
    return (
      <IonCard className="acceptance">
        <IonCardHeader><IonCardTitle>Customer acceptance</IonCardTitle></IonCardHeader>
        <IonCardContent>
          <div className="acceptance-done lost">
            <strong>Declined {shortDate(estimate.declined_at)}</strong> · {REASONS.find((r) => r[0] === estimate.decline_reason)?.[1]}{estimate.decline_note ? ` · ${estimate.decline_note}` : ''}
          </div>
        </IonCardContent>
      </IonCard>
    );
  }

  const accept = async () => {
    if (!name.trim()) { notify("Enter the customer's name."); return; }
    const sig = pad.current?.toDataUrl();
    if (!sig) { notify('The customer needs to sign in the box.'); return; }
    if (!agree) { notify('The customer needs to tick the acceptance box.'); return; }
    setBusy(true);
    try {
      onDone(await serviceApi.accept(estimate.id, name.trim(), title.trim(), sig));
      notify('Accepted and signed. The signed PDF is ready.');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not save the signature.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!reason) { notify('Pick the main reason.'); return; }
    setBusy(true);
    try {
      onDone(await serviceApi.decline(estimate.id, reason, note));
      notify('Decline recorded.');
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not record the decline.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <IonCard className="acceptance">
      <IonCardHeader><IonCardTitle>Customer acceptance</IonCardTitle></IonCardHeader>
      <IonCardContent className="acceptance-body">
        <p>Hand the device to the customer to review the proposal above and sign.</p>
        <div className="acceptance-grid">
          <IonItem><IonInput label="Customer name" labelPlacement="stacked" value={name} onIonInput={(e) => setName(String(e.detail.value ?? ''))} /></IonItem>
          <IonItem><IonInput label="Title" labelPlacement="stacked" placeholder="e.g. Facilities Director" value={title} onIonInput={(e) => setTitle(String(e.detail.value ?? ''))} /></IonItem>
        </div>
        <SignaturePad ref={pad} />
        <IonItem lines="none">
          <IonCheckbox checked={agree} onIonChange={(e) => setAgree(e.detail.checked)} labelPlacement="end" justify="start">
            <span className="acceptance-agree">I accept proposal {estimate.acs_number} for {money(estimate.total_price)} and authorize Alpha Controls &amp; Services to perform the work described.</span>
          </IonCheckbox>
        </IonItem>
        <div className="acceptance-actions">
          <IonButton onClick={accept} disabled={busy}>Accept and sign</IonButton>
          <IonButton fill="outline" onClick={() => setDeclining(true)} disabled={busy}>Customer declines</IonButton>
        </div>
        {declining && (
          <div className="acceptance-decline">
            <IonItem>
              <IonSelect label="Main reason" labelPlacement="stacked" value={reason} onIonChange={(e) => setReason(e.detail.value as DeclineReason)}>
                {REASONS.map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
              </IonSelect>
            </IonItem>
            <IonItem><IonInput label="Note (optional)" labelPlacement="stacked" value={note} onIonInput={(e) => setNote(String(e.detail.value ?? ''))} /></IonItem>
            <div className="acceptance-actions">
              <IonButton fill="outline" onClick={decline} disabled={busy}>Record decline</IonButton>
              <IonButton fill="clear" onClick={() => setDeclining(false)}>Cancel</IonButton>
            </div>
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default AcceptancePanel;
